import React from 'react'
import { Box, Text, Button, Icon, ClickableContent, Dropdown } from '@deseretbook/react-ui'
import { useDropdown } from '@deseretbook/react-utils'

import { HIGHLIGHT_COLOR_OPTIONS } from '../../../utils/constants'
import { formatDateString } from '../../../utils/base'
import { truncateText } from '../../../utils/truncateText'

import './MarkupsSidebar.css'

const markupColor = (m) =>
  HIGHLIGHT_COLOR_OPTIONS.find((c) => c.value === (m.highlight && m.highlight.color))

// Define the structure for different markup types
const MARKUP_TYPES = {
  highlights: {
    label: 'Highlight',
    getColor: (m) => markupColor(m),
    getBody: (m) => m.highlight && m.highlight.content,
    getStart: (m) => m.highlight && m.highlight.start,
  },
  notes: {
    label: 'Note',
    getColor: (m) => markupColor(m),
    getBody: (m) => (m.note && m.note.content) || (m.note && m.note.text),
    getStart: (m) => (m.highlight && m.highlight.start) || (m.note && m.note.start),
  },
  tags: {
    label: 'Tag',
    getColor: (m) => markupColor(m),
    getBody: (m) => (m.tag && m.tag.label) || m.tag,
    getStart: (m) => (m.highlight && m.highlight.start) || (m.tag && m.tag.start),
  },
}

/**
 * Markup component to display highlights, notes, or tags in the book reader.
 */
const Markup = ({
  markup,
  tableOfContents,
  onMarkupClick,
  onRemoveMarkup,
  markupType, // 'highlights' | 'notes' | 'tags'
  onMarkupEdit,
  handleClose,
}) => {
  const cfg = MARKUP_TYPES[markupType]
  const dropdown = useDropdown()

  /* ---------- derived values ---------- */
  const colorObj = cfg.getColor(markup)
  const body = cfg.getBody(markup)
  const locationStart = cfg.getStart(markup)
  const date = formatDateString(markup.changed, true)

  const chapter = tableOfContents.byDocumentId[markup.documentId] || {}

  /* ---------- handlers ---------- */
  const goToMarkup = () => onMarkupClick(locationStart, markup.documentId)
  const handleRemoveMarkup = () => onRemoveMarkup(markup)

  return (
    <div>
      <Box
        alignItems="center"
        justifyContent="space-between"
        style={{
          margin: '16px 0',
          borderBottom: '1px solid #0000001A',
          paddingBottom: '16px',
        }}
      >
        {/* colored dot (highlights only) */}
        <Box style={{ width: '1rem', opacity: 0.7 }}>
          {colorObj && (
            <>
              {cfg.label === 'Note' ? (
                <Icon
                  className="markup-color-icon"
                  name="noteSolid"
                  style={{ height: '16px', width: '16px' }}
                  svgProps={{
                    fill: colorObj.hex,
                    style: { fill: colorObj.hex, color: colorObj.hex },
                    height: '16px',
                    width: '16px',
                  }}
                />
              ) : (
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="8" cy="8.5" r="8" fill={colorObj.hex} />
                </svg>
              )}
            </>
          )}
        </Box>

        {/* text block */}
        <Box
          isColumn
          alignItems="start"
          justifyContent="start"
          style={{ margin: '0 12px' }}
          isFullwidth
        >
          <Text
            title={chapter.label}
            style={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500 }}
          >
            {truncateText(chapter.label || '', 70)}
          </Text>

          {/* main body (highlight text, note text, or tag) */}
          {body && (
            <Text
              title={body}
              style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: 16,
                fontWeight: 400,
                margin: '8px 0',
              }}
            >
              {truncateText(body, 100)}
            </Text>
          )}

          <Text
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              fontWeight: 400,
              color: 'rgba(0, 0, 0, 0.65)',
            }}
          >
            {date}
          </Text>
        </Box>

        {/* kebab-menu trigger */}
        <Box
          alignItems="center"
          justifyContent="center"
          style={{ minWidth: '2rem' }}
          {...dropdown.triggerProps}
          tabIndex={0}
        >
          <ClickableContent isPaddingless>
            <Icon name="options" />
          </ClickableContent>
        </Box>
      </Box>

      {/* dropdown menu */}
      <Dropdown {...dropdown.dropdownProps} className="dropdown">
        <Box isColumn alignItems="start" className="dropdownContainer">
          <Button onClick={goToMarkup} className="dropdownButton">
            <Icon
              className="bookmark-dropdown-icon"
              name="arrowRight"
              style={{ marginRight: '0.5rem' }}
            />
            View in Book
          </Button>
          <Button
            onClick={() => {
              handleClose()
              onMarkupEdit(markup)
            }}
            className="dropdownButton"
            style={{ marginTop: '8px' }}
          >
            <Icon
              className="bookmark-dropdown-icon"
              name="editOutline"
              style={{ marginRight: '0.5rem' }}
            />
            Edit
          </Button>
          <Button
            onClick={() => {
              handleClose()
              handleRemoveMarkup()
            }}
            className="dropdownButton"
            style={{ marginTop: '8px' }}
          >
            <Icon
              className="bookmark-dropdown-icon"
              name="trashOutline"
              style={{ marginRight: '0.5rem' }}
            />
            Remove
          </Button>
        </Box>
      </Dropdown>
    </div>
  )
}

export default Markup
